import useRequest from "@/lib/request/useRequest";
import { useTranslation } from "react-i18next";
import { useCallback, useState, useMemo } from "react";
import { FieldConfig } from "@/types/fieldConfig";
import { RxFileFormat } from "@/types/enums";
import {
    Button,
    ModalContent,
    ModalBody,
    ModalHeader,
    Modal,
    Autocomplete,
    AutocompleteItem,
    addToast,
    Link,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileZipper } from "@fortawesome/free-solid-svg-icons";
import { AxiosResponse } from "axios";

interface DownloadDetails {
    ok: number[];
    errors: number[];
}

function BulkRxExport({
    selectedFields,
    onFinish,
}: {
    selectedFields: FieldConfig[];
    onFinish: () => void;
}) {
    const { axios } = useRequest();
    const { t } = useTranslation("messages");

    const [fileFormat, setFileFormat] = useState<RxFileFormat | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [downloadDetails, setDownloadDetails] =
        useState<DownloadDetails | null>(null);

    const failedFields = useMemo(() => {
        if (!downloadDetails) return [];
        return selectedFields.filter((field) =>
            downloadDetails.errors.includes(
                field.latest_plot_prescription_id as number,
            ),
        );
    }, [downloadDetails, selectedFields]);

    const handleBulkExportRx = useCallback(async () => {
        const prescriptionIds = selectedFields
            .map((field) => field.latest_plot_prescription_id)
            .filter((id) => id !== null);

        setIsLoading(true);
        setDownloadDetails(null);

        try {
            const response: AxiosResponse<Blob> = await axios.put(
                "prescriptions/bulk_download/",
                {
                    prescription_ids: prescriptionIds,
                    file_format: fileFormat,
                },
                {
                    responseType: "blob",
                },
            );

            const downloadDetailsHeader =
                response.headers["x-download-details"];
            let downloadDetails: DownloadDetails | null = null;

            if (downloadDetailsHeader) {
                try {
                    downloadDetails = JSON.parse(
                        downloadDetailsHeader.replace(/'/g, '"'),
                    );
                } catch (e) {
                    console.error("Failed to parse download details header", e);
                }
            }

            if (
                downloadDetails &&
                downloadDetails.errors &&
                downloadDetails.errors.length > 0
            ) {
                setDownloadDetails(downloadDetails);
                setIsLoading(false);
                return;
            }

            const url = window.URL.createObjectURL(
                new Blob([response.data], {
                    type: "application/zip",
                }),
            );
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `sentinel_plot_rx_bulk_export.zip`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);

            addToast({
                title: t("success.exported_rx_bulk"),
                timeout: 3000,
                color: "success",
            });

            onFinish();
        } catch {
            addToast({
                title: t("error.failed_to_export"),
                timeout: 3000,
                color: "danger",
            });
            setIsLoading(false);
        }
    }, [axios, selectedFields, fileFormat, onFinish, t]);

    return (
        <Modal isOpen size="xl" onClose={onFinish}>
            <ModalContent>
                <ModalHeader>Bulk Export Plot Rx</ModalHeader>
                <ModalBody>
                    <div className="flex flex-col gap-2">
                        {failedFields.length > 0 ? (
                            <div className="mt-4 p-4 bg-danger-50 border border-danger-200 rounded-lg">
                                <h3 className="text-danger-700 font-semibold mb-2">
                                    Failed to export prescriptions for the
                                    following fields:
                                </h3>
                                <ul className="space-y-2">
                                    {failedFields.map(
                                        (failedField: FieldConfig) => (
                                            <li
                                                key={failedField.id}
                                                className="text-sm"
                                            >
                                                <Link
                                                    href={`/dashboard/fields/${failedField.id}/rx/${failedField.latest_plot_prescription_id}`}
                                                    className="text-danger-600 hover:underline"
                                                    target="_blank"
                                                >
                                                    {failedField.name}
                                                </Link>
                                            </li>
                                        ),
                                    )}
                                </ul>
                            </div>
                        ) : (
                            <>
                                <Autocomplete
                                    label={t("file_format", { ns: "rx" })}
                                    placeholder={t("optional", { ns: "rx" })}
                                    onSelectionChange={(item) => {
                                        setFileFormat(item as RxFileFormat);
                                    }}
                                >
                                    {Object.values(RxFileFormat).map(
                                        (format) => (
                                            <AutocompleteItem
                                                key={format as string}
                                            >
                                                {format as string}
                                            </AutocompleteItem>
                                        ),
                                    )}
                                </Autocomplete>
                                <Button
                                    color="primary"
                                    startContent={
                                        <FontAwesomeIcon icon={faFileZipper} />
                                    }
                                    onPress={handleBulkExportRx}
                                    isLoading={isLoading}
                                    className="mt-4"
                                >
                                    {t("export", { ns: "rx" })}
                                </Button>
                            </>
                        )}
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}

export default BulkRxExport;
